import AppComponent from '../base/app-component.ts';

interface OperatoreItem {
  idFenice: number;
  nome: string;
  cognome: string;
  territorio: string | null;
}

declare const appBranding: { nomeBreve?: string } | undefined;

/**
 * wc-operatore-combobox — Selezione operatore con due modalità:
 *
 * 1. SELECT (default): dropdown con operatori del territorio locale
 * 2. SEARCH: typeahead su operatori di altri territori (master.fenice_users)
 *
 * Emette CustomEvent 'operatore-change' con detail: { idFenice, cognome, nome, territorio } | null.
 *
 * Attributi:
 *   value             (opzionale) ID Fenice preselezionato. Se appartiene ai
 *                     locali viene selezionato nella SELECT; altrimenti viene
 *                     risolto via lookup e mostrato nella modalità SEARCH.
 *   label-territorio  (opzionale) etichetta del territorio locale; default appBranding.nomeBreve
 */
export class OperatoreCombobox extends AppComponent {
  static override componentName = 'wc-operatore-combobox';
  static override observedAttributes = ['value'];
  protected override get templateId() { return 'operatore-combobox'; }

  private operatoriLocali: OperatoreItem[] = [];
  private selected: OperatoreItem | null = null;
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  private rendered = false;
  private localiLoaded = false;

  protected override buildRenderingData(): Record<string, unknown> {
    const fallback = typeof appBranding !== 'undefined' ? appBranding?.nomeBreve ?? '' : '';
    return {
      ...super.buildRenderingData(),
      labelTerritorio: this.attributeString('label-territorio', fallback),
    };
  }

  protected override afterRender(): void {
    this.setupModeSwitch();
    this.setupSelect();
    this.setupSearch();
    this.setupClear();
    this.rendered = true;
    void this.initLocaliAndPreset();
  }

  protected override onAttributeChanged(name: string): void {
    if (!this.rendered || !this.localiLoaded) return;
    if (name !== 'value') return;
    void this.applyPresetFromAttributes();
  }

  private async initLocaliAndPreset(): Promise<void> {
    await this.loadLocali();
    this.localiLoaded = true;
    await this.applyPresetFromAttributes();
  }

  private async applyPresetFromAttributes(): Promise<void> {
    const preId = this.attributeNumber('value');
    if (!preId) return;
    if (this.selected?.idFenice === preId) return;

    const local = this.operatoriLocali.find(o => o.idFenice === preId);
    if (local) {
      this.applyLocalSelection(local);
      return;
    }
    const remote = await this.fetchOperatoreById(preId);
    if (!remote) return;
    this.showMode('search');
    const label = `${remote.cognome} ${remote.nome}${remote.territorio ? ` (${remote.territorio})` : ''}`;
    this.confirmSelection(remote, label);
  }

  private applyLocalSelection(op: OperatoreItem): void {
    this.selected = op;
    const select = this.$('[data-operatore-select]') as HTMLSelectElement | null;
    if (select) select.value = String(op.idFenice);
    const hidden = this.$('[data-operatore-value]') as HTMLInputElement | null;
    if (hidden) hidden.value = String(op.idFenice);
    this.dispatch('operatore-change', { idFenice: op.idFenice, nome: op.nome, cognome: op.cognome, territorio: op.territorio });
  }

  private async fetchOperatoreById(id: number): Promise<OperatoreItem | null> {
    const r = await this.api.operatori.dettaglio(id);
    return r.ok ? r.data : null;
  }

  // --- Getter pubblici ---

  get value(): number | null {
    return this.selected?.idFenice ?? null;
  }

  get operatoreNome(): string | null {
    if (!this.selected) return null;
    return `${this.selected.cognome} ${this.selected.nome}`.trim();
  }

  // --- Caricamento operatori locali ---

  private async loadLocali(): Promise<void> {
    const result = await this.api.operatori.lista();
    if (!result.ok) return;
    this.operatoriLocali = result.data.operatori;
    this.populateSelect();
  }

  private populateSelect(): void {
    const select = this.$('[data-operatore-select]') as HTMLSelectElement | null;
    if (!select) return;
    while (select.options.length > 1) select.remove(1);
    for (const op of this.operatoriLocali) {
      const option = document.createElement('option');
      option.value = String(op.idFenice);
      option.textContent = `${op.cognome} ${op.nome}`;
      select.appendChild(option);
    }
  }

  // --- Switch modalità ---

  private setupModeSwitch(): void {
    this.delegate.on('click', '[data-action="mode-search"]', () => this.showMode('search'));
    this.delegate.on('click', '[data-action="mode-select"]', () => this.showMode('select'));
  }

  private showMode(mode: 'select' | 'search'): void {
    const selectDiv = this.$('[data-mode="select"]');
    const searchDiv = this.$('[data-mode="search"]');
    if (!selectDiv || !searchDiv) return;

    selectDiv.classList.toggle('d-none', mode !== 'select');
    searchDiv.classList.toggle('d-none', mode !== 'search');

    if (mode === 'search') {
      const input = this.$('[data-operatore-input]') as HTMLInputElement | null;
      if (input) { input.value = ''; input.focus(); }
    }
  }

  // --- Modalità SELECT ---

  private setupSelect(): void {
    const select = this.$('[data-operatore-select]') as HTMLSelectElement | null;
    if (!select) return;

    this.listen(select, 'change', () => {
      const id = Number(select.value);
      if (!id) return;
      const op = this.operatoriLocali.find(o => o.idFenice === id);
      if (op) {
        const label = `${op.cognome} ${op.nome}`;
        this.confirmSelection(op, label);
      }
    });
  }

  // --- Modalità SEARCH ---

  private setupSearch(): void {
    const input = this.$('[data-operatore-input]') as HTMLInputElement | null;
    if (!input) return;

    this.listen(input, 'input', () => {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      const q = input.value.trim();
      if (q.length < 3) {
        this.hideDropdown();
        return;
      }
      this.searchTimeout = setTimeout(() => this.doSearch(q), 300);
    });

    this.listen(input, 'keydown', (e: Event) => {
      if ((e as KeyboardEvent).key === 'Escape') this.hideDropdown();
    });
  }

  private async doSearch(q: string): Promise<void> {
    const result = await this.api.operatori.cerca(q);
    if (!result.ok) return;

    const { locali, remoti } = result.data;
    const all = [...locali, ...remoti];
    this.showDropdown(all);
  }

  private showDropdown(items: OperatoreItem[]): void {
    const dropdown = this.$('[data-operatore-dropdown]');
    if (!dropdown) return;

    if (items.length === 0) {
      dropdown.innerHTML = '<div class="dropdown-item text-muted">Nessun risultato</div>';
      dropdown.classList.add('show');
      return;
    }

    dropdown.innerHTML = items.map(op => {
      const territorio = op.territorio ? ` <small class="text-muted">(${op.territorio})</small>` : '';
      return `<a class="dropdown-item" href="#" data-op-id="${op.idFenice}">${op.cognome} ${op.nome}${territorio}</a>`;
    }).join('');
    dropdown.classList.add('show');

    this.delegate.offAll('click', '[data-op-id]');
    this.delegate.on('click', '[data-op-id]', (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-op-id]') as HTMLElement;
      const id = Number(el.dataset.opId);
      const op = items.find(o => o.idFenice === id);
      if (op) this.selectFromSearch(op);
    });
  }

  private hideDropdown(): void {
    const dropdown = this.$('[data-operatore-dropdown]');
    if (dropdown) dropdown.classList.remove('show');
  }

  private async selectFromSearch(op: OperatoreItem): Promise<void> {
    this.hideDropdown();
    // Salva in cache se non è già locale
    if (!this.operatoriLocali.some(o => o.idFenice === op.idFenice)) {
      await this.api.operatori.salvaCache(op);
      this.operatoriLocali.push(op);
      this.populateSelect();
    }
    const label = `${op.cognome} ${op.nome}${op.territorio ? ` (${op.territorio})` : ''}`;
    this.confirmSelection(op, label);
  }

  // --- Conferma / Clear ---

  private confirmSelection(op: OperatoreItem, label: string): void {
    this.selected = op;

    const hidden = this.$('[data-operatore-value]') as HTMLInputElement | null;
    if (hidden) hidden.value = String(op.idFenice);

    // Nascondi entrambe le modalità, mostra conferma
    this.$('[data-mode="select"]')?.classList.add('d-none');
    this.$('[data-mode="search"]')?.classList.add('d-none');
    const confirmed = this.$('[data-operatore-confirmed]');
    if (confirmed) {
      confirmed.classList.remove('d-none');
      const labelEl = confirmed.querySelector('[data-operatore-label]');
      if (labelEl) labelEl.textContent = label;
    }

    this.dispatch('operatore-change', { idFenice: op.idFenice, nome: op.nome, cognome: op.cognome, territorio: op.territorio });
  }

  private setupClear(): void {
    this.delegate.on('click', '[data-action="clear-operatore"]', (e: Event) => {
      e.preventDefault();
      this.clearSelection();
    });
  }

  clearSelection(): void {
    this.selected = null;

    const hidden = this.$('[data-operatore-value]') as HTMLInputElement | null;
    if (hidden) hidden.value = '';

    // Reset select
    const select = this.$('[data-operatore-select]') as HTMLSelectElement | null;
    if (select) select.value = '';

    // Torna a modalità select
    this.$('[data-operatore-confirmed]')?.classList.add('d-none');
    this.showMode('select');

    this.dispatch('operatore-change', null);
  }
}
